<script lang="ts">
	import { ClientVelatlasLoggerApi } from '$lib/api/velatlas/velatlas.loggerApi.client';
	import { ANALYTICS_EVENT_NAMES } from '$lib/statics';
	import { onMount } from 'svelte';

	const {
		text,
		maxLines = 3,
		class: className = ''
	}: {
		text: string;
		maxLines?: number;
		class?: string;
	} = $props();

	const analyticsApi = new ClientVelatlasLoggerApi();

	let isExpanded = $state<boolean>(false);
	let isOverflowing = $state<boolean>(false);
	let containerRef = $state<HTMLDivElement>();
	let textRef = $state<HTMLParagraphElement>();

	let collapsedHeight = $derived<number>(maxLines * 28);
	let contentHeight = $derived<number>(maxLines * 28);

	function measure() {
		if (!textRef) return;

		const styles = getComputedStyle(textRef);

		// Convert the actual CSS line-height into pixels.
		const lineHeight = parseFloat(styles.lineHeight);

		if (!Number.isFinite(lineHeight)) return;

		collapsedHeight = lineHeight * maxLines;

		// scrollHeight gives us the full un-clipped content height.
		contentHeight = textRef.scrollHeight;

		isOverflowing = contentHeight > collapsedHeight + 1;

		// If the content has become short enough while expanded, make sure we don't leave it in an inconsistent state.
		if (!isOverflowing && isExpanded) {
			isExpanded = false;
		}
	}

	function toggle() {
		if (!containerRef) return;

		if (!isExpanded) {
			isExpanded = true;
			return;
		}

		// Collapsing: first scroll the container into position, then collapse once the scroll has had time to settle.
		const rect = containerRef.getBoundingClientRect();
		const isAlreadyInPosition = Math.abs(rect.top) < 4;

		const collapse = () => {
			isExpanded = false;
		};

		if (isAlreadyInPosition) {
			collapse();
			return;
		}

		containerRef.scrollIntoView({
			behavior: 'smooth',
			block: 'start'
		});

		setTimeout(collapse, 350);
	}

	$effect(() => {
		if (isExpanded) {
			analyticsApi.sendAnalytics({
				event: ANALYTICS_EVENT_NAMES.readMore,
				payload: { type: 'text' }
			});
		}
	});

	onMount(() => {
		if (!textRef) return;

		const resizeObserver = new ResizeObserver(() => {
			measure();
		});

		resizeObserver.observe(textRef);

		requestAnimationFrame(measure);

		// Re-measure after fonts have loaded because font metrics can
		// change the number of wrapped lines.
		document.fonts?.ready.then(measure);
		return () => {
			resizeObserver.disconnect();
		};
	});
</script>

<div bind:this={containerRef} class="relative flex scroll-mt-44 flex-col items-center justify-center lg:scroll-mt-32 {className}">
	<div
		class="relative overflow-hidden transition-[max-height] duration-500 ease-in-out"
		style:max-height={`${isExpanded ? contentHeight : collapsedHeight}px`}
		style:mask-image={isOverflowing && !isExpanded ? 'linear-gradient(to bottom, black 65%, transparent 100%)' : 'none'}
		style:-webkit-mask-image={isOverflowing && !isExpanded ? 'linear-gradient(to bottom, black 65%, transparent 100%)' : 'none'}
	>
		<p bind:this={textRef} class="leading-relaxed whitespace-pre-line">
			{text}
		</p>
	</div>

	{#if isOverflowing}
		<button
			class="mt-2 cursor-pointer text-xs font-bold tracking-widest text-primary hover:underline"
			type="button"
			onclick={toggle}
		>
			{isExpanded ? 'SHOW LESS' : 'READ MORE'}
		</button>
	{/if}
</div>
